<template>
  <div class="v2-cart-total">
    <!-- Top actions: Import / Export (hidden in popup mode — buttons are in V2BrandCart) -->
    <div
      v-if="showTotal && !isPopup"
      class="v2-cart-total__top-actions"
    >
      <v2-button
        variant="outline"
        size="sm"
        @click="openImportModal"
      >
        Import
      </v2-button>
      <v2-button
        variant="outline"
        size="sm"
        @click="onClickExportCart"
      >
        Export
      </v2-button>
    </div>

    <!-- Two-column: Notes | Order Details -->
    <div
      v-if="showTotal"
      class="v2-cart-total__grid"
    >
      <!-- Notes & Shipping card -->
      <div class="v2-cart-total__section">
        <div class="v2-cart-total__section-header">
          <h4 class="v2-cart-total__section-title">
            Notes
          </h4>
          <v2-button
            v-if="isEditNotes && isRetailer"
            variant="primary"
            size="sm"
            :disabled="isSavingNotes"
            @click="onUpdateNotes"
          >
            {{ isSavingNotes ? 'Saving...' : 'Save' }}
          </v2-button>
          <button
            v-if="!isEditNotes"
            class="v2-cart-total__edit-btn"
            @click="handleNotesEdit"
          >
            <svg
              width="15"
              height="15"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
            >
              <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
              <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
            </svg>
          </button>
        </div>

        <textarea
          v-if="isEditNotes"
          v-model="notes"
          class="v2-cart-total__notes-input"
          placeholder="Enter Notes"
        />
        <span
          v-else
          class="v2-cart-total__notes-text"
        >{{ notes }}</span>

        <!-- Delivery window (retailer, or brand creating order for retailer) -->
        <div
          v-if="isRetailer || isPopup"
          class="v2-cart-total__field"
        >
          <span>Requested Delivery Window:</span>
          <span v-if="!isEditDeliveryWindow">
            {{ expectedDeliveryDate || '—' }}
            <button
              class="v2-cart-total__edit-btn"
              style="display: inline-flex; vertical-align: middle"
              @click="handleDeliveryWindowEdit"
            >
              <svg
                width="13"
                height="13"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
              >
                <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
              </svg>
            </button>
          </span>
          <div
            v-if="isEditDeliveryWindow"
            class="v2-cart-total__delivery-edit"
          >
            <flat-pickr
              ref="deliveryWindowDate"
              v-model="expectedDeliveryDate"
              class="form-control"
              style="max-width: 140px; font-size: 0.8125rem"
              :config="VUE_FLATPICKR_MONTH_CONFIG"
              :placeholder="KP_MONTH_FORMAT.PLACEHOLDER"
            />
            <v2-button
              variant="primary"
              size="sm"
              :disabled="isSavingDeliveryWindow"
              @click="onUpdateDeliveryWindow"
            >
              {{ isSavingDeliveryWindow ? 'Saving...' : 'Save' }}
            </v2-button>
          </div>
        </div>

        <!-- Shipping address (popup mode: read-only) -->
        <div
          v-if="isPopup && displayShippingAddress"
          class="v2-cart-total__address-block"
        >
          <strong>Shipping Address</strong>
          <address-detail :address="displayShippingAddress" />
        </div>
      </div>

      <!-- Order Details card -->
      <div class="v2-cart-total__section">
        <div class="v2-cart-total__section-header">
          <h4 class="v2-cart-total__section-title">
            Order Details{{ currency ? ` (${currency})` : '' }}
          </h4>
        </div>

        <div class="v2-cart-total__totals">
          <div class="v2-cart-total__total-row">
            <span>Total Value</span>
            <span>{{ formatCurrency(totalMRP, currency) }}</span>
          </div>
          <div class="v2-cart-total__total-row">
            <span>Total Units</span>
            <span>{{ formatNumber(totalUnits) }}</span>
          </div>

          <!-- Credits -->
          <div
            v-if="discountInCredit > 0"
            class="v2-cart-total__credits"
          >
            <div class="v2-cart-total__credits-label">
              Available Credit Points
              <span
                v-if="!showCreditInput"
                @click="showCreditInput = true"
              >
                {{ creditsUsed > 0 ? 'Edit' : 'Use credit points' }}
              </span>
            </div>
            <div class="v2-cart-total__total-row">
              <span>{{ remainingCredits }} credits available</span>
              <span v-if="!showCreditInput && appliedCredits > 0">-{{ formatCurrency(appliedCredits, currency) }}</span>
            </div>
            <div
              v-if="showCreditInput"
              class="v2-cart-total__credits-input-row"
            >
              <input
                v-model.number="creditsUsed"
                type="number"
                class="v2-cart-total__credits-input"
                :max="getMaxCreditEligible"
                min="0"
              >
              <v2-button
                variant="primary"
                size="sm"
                @click="applyCredits"
              >
                Apply
              </v2-button>
            </div>
          </div>

          <div class="v2-cart-total__divider" />

          <div class="v2-cart-total__total-row v2-cart-total__total-row--bold">
            <span>Total</span>
            <span>{{ formatCurrency(total, currency) }}</span>
          </div>
        </div>

        <!-- Terms & Conditions -->
        <div class="v2-cart-total__terms">
          <div class="v2-cart-total__terms-row">
            <label class="v2-cart-total__terms-checkbox">
              <input
                v-model="acceptTerms"
                type="checkbox"
                @change="trackTermsAndConditionsCheck($event.target.checked)"
              >
              I agree to the
            </label>
            <span
              class="v2-cart-total__terms-link"
              @click="handleTermsAndConditionLink(true)"
            >
              terms and conditions
            </span>
          </div>
          <div
            v-if="isTermsUnChecked && !acceptTerms"
            class="v2-cart-total__terms-error"
          >
            Please accept the Terms and Conditions
          </div>
        </div>

        <!-- Place Order -->
        <div class="v2-cart-total__actions">
          <v2-button
            variant="primary"
            :loading="isPlacingOrder"
            :disabled="isDisabledPlaceOrder || isDirectRetailerNotSelected"
            style="width: 100%"
            @click="handlePlaceOrder"
          >
            Place Order
          </v2-button>
        </div>
      </div>
    </div>

    <!-- Cart Products -->
    <v2-cart-products
      ref="cartProducts"
      :is-popup="isPopup"
      :modal-id="modalId"
      :retailer-id="retailerId"
      :cart-products="cartProducts"
      @remove-product-selected-from-brand="removeProductSelectedFromBrand"
      @update-total="updateCartTotal"
      @update-total-units="updateCartTotalUnits"
      @has-cart-product="updateListView"
      @update-notes="(note) => { notes = note }"
      @remove-multiple-products="removeMultipleProductsFromBrand"
    />

    <!-- V2 Terms Modal (replaces Bootstrap CartTerms) -->
    <v2-modal
      :show="showTermsModal"
      title="Terms and Conditions"
      size="xl"
      @close="showTermsModal = false; handleTermsModalClose()"
    >
      <div class="v2-cart-terms">
        <div class="v2-cart-terms__heading">
          GENERAL SALE CONDITIONS
        </div>
        <div
          v-for="section in cartTermsContent"
          :key="section.title"
          class="v2-cart-terms__section"
        >
          <div class="v2-cart-terms__section-title">
            {{ section.title }}
          </div>
          <p
            v-for="point in section.points"
            :key="point"
            class="v2-cart-terms__point"
          >
            {{ point }}
          </p>
        </div>
      </div>
      <template #footer>
        <v2-button
          variant="outline"
          @click="showTermsModal = false; handleTermsModalClose()"
        >
          Close
        </v2-button>
        <v2-button
          variant="primary"
          @click="updateTerms(); showTermsModal = false"
        >
          Accept
        </v2-button>
      </template>
    </v2-modal>

    <!-- V2 sub-modals (Import, Clear Cart, Clear Retailer Cart, Conflict) -->
    <v2-cart-modals
      ref="cartModals"
      import-cart-modal-id="import_cart_modal"
      :clear-cart="clearCart"
      :is-popup="isPopup"
      :retailer-id="retailerId"
      :on-cart-import-success="onCartImportSuccess"
      :place-order="placeOrder"
      :cart-products="$refs.cartProducts"
      :on-click-export-cart="onClickExportCart"
      @on-close-import-cart-modal="trackImportCartEvents('close')"
    />
    <!-- V2 Export modal -->
    <v2-cart-export-modal
      ref="exportModal"
      :retailer-id="isPopup ? retailerId : null"
      @on-cart-export-success="onSuccessCartExport"
    />

    <!-- V2 Order Fail Modal -->
    <v2-modal
      :show="showOrderFailModal"
      title="Order Creation Failed"
      size="md"
      @close="showOrderFailModal = false"
    >
      <order-validation-message
        v-if="profileData"
        :profile-data="profileData"
      />
      <template #footer>
        <v2-button
          variant="primary"
          @click="$router.push({ name: 'profile' })"
        >
          Edit Profile
        </v2-button>
      </template>
    </v2-modal>
  </div>
</template>

<script>
import { getCurrentInstance, ref } from '@vue/composition-api'
import flatPickr from 'vue-flatpickr-component'
import V2Button from '@/components/v2/V2Button.vue'
import V2Modal from '@/components/v2/V2Modal.vue'
import { useCartTotal } from '@/composables/useCartTotal'
import UserRoleMixin from '@/views/apps/UserRoleMixin.vue'
import AddressMixinVue from '@/views/apps/account-setting/multiple-address/AddressMixin.vue'
import NotificationMixin from '@/views/mixins/NotificationMixin.vue'
import AddressDetail from '@/views/apps/account-setting/multiple-address/AddressDetail.vue'
import OrderValidationMessage from '@/views/apps/checkout/OrderValidationMessage.vue'
import cartTermsContent from '@/views/apps/checkout/cart-terms-content'
import V2CartProducts from './V2CartProducts.vue'
import V2CartModals from './V2CartModals.vue'
import V2CartExportModal from './V2CartExportModal.vue'

export default {
  name: 'V2CartTotal',
  components: {
    V2Button,
    V2Modal,
    V2CartProducts,
    V2CartModals,
    V2CartExportModal,
    AddressDetail,
    OrderValidationMessage,
    flatPickr,
  },
  mixins: [UserRoleMixin, AddressMixinVue, NotificationMixin],
  props: {
    isPopup: {
      type: Boolean,
      default: false,
    },
    cartProducts: {
      type: Array,
      default: () => [],
    },
    retailerId: {
      type: String,
      default: null,
    },
    brandNotes: {
      type: Object,
      default: () => {},
    },
    getCartProductsByBrand: {
      type: Function,
      default: () => {},
    },
    modalId: {
      type: String,
      default: '',
    },
  },
  emits: ['remove-product-selected-from-brand', 'update-notes', 'remove-multiple-products'],
  setup(props, { emit }) {
    const showOrderFailModal = ref(false)
    const showTermsModal = ref(false)

    // Use getCurrentInstance to access $bvModal and $refs after mount
    const vm = getCurrentInstance()?.proxy

    // Map of modal id → show handler (replaces if-else chain in onShowSubModal)
    const showModalHandlers = {
      'modal-order-fail': () => { showOrderFailModal.value = true },
      'cart-terms': () => { showTermsModal.value = true },
      cart_export_modal: () => { vm?.$refs?.exportModal?.show() },
      'clear-cart': id => { vm?.$refs?.cartModals?.show(id) },
      import_cart_modal: id => { vm?.$refs?.cartModals?.show(id) },
    }

    function onShowSubModal(id) {
      const handler = showModalHandlers[id]
      if (handler) {
        handler(id)
        return
      }
      // Fallback: Bootstrap modals (e.g. clear-retailer-cart from composable)
      vm?.$bvModal?.show(id)
    }

    // Set of modal ids owned by V2CartModals (replaces if-else chain in onHideSubModal)
    const cartModalsOwnedIds = new Set([
      'clear-cart',
      'import_cart_modal',
      'clear-retailer-cart',
      'modal-upload-cart-conflict',
    ])

    function onHideSubModal(id) {
      if (cartModalsOwnedIds.has(id)) {
        vm?.$refs?.cartModals?.hide(id)
        return
      }
      vm?.$bvModal?.hide(id)
    }

    function getCartProductsRef() {
      return vm?.$refs?.cartProducts || null
    }

    function onOrderSuccess(orderId, orderSeqId) {
      if (vm?.$root?.$orderSuccessPopupContext) {
        vm.$root.$orderSuccessPopupContext.value = {
          orderIds: [{ orderSeqId }],
        }
      }
      if (vm?.$router) {
        vm.$router.push({
          name: 'v2-order/detail',
          params: { orderId },
        })
      }
    }

    const cartTotal = useCartTotal(props, {
      onShowSubModal,
      onHideSubModal,
      getCartProductsRef,
      emit,
      onOrderSuccess,
    })

    return {
      showOrderFailModal,
      showTermsModal,
      cartTermsContent,
      ...cartTotal,
    }
  },
  computed: {
    displayShippingAddress() {
      const list = (this.addresses || []).filter(a => a?.addressType === this.ADDRESSES_TYPE.SHIPPING)
      const fromList = list.find(a => a?.isPrimary && a?.addressLine1) || list.find(a => a?.addressLine1)
      if (fromList) return fromList
      const legacy = this.profileData?.shippingAddress
      return legacy?.addressLine1 ? legacy : null
    },
  },
  mounted() {
    this.initialize()
  },
  methods: {
    handlePlaceOrder() {
      // Activate error display first so zero-qty banners become visible
      this.$refs.cartProducts?.activateErrors()
      this.placeOrder(false)
    },
    openImportModal() {
      this.trackImportCartEvents('button-click')
      this.$refs.cartModals?.show('import_cart_modal')
    },
    handleDeliveryWindowEdit() {
      this.isEditDeliveryWindow = true
      this.$nextTick(() => {
        if (this.$refs.deliveryWindowDate?.$el?._flatpickr) {
          this.$refs.deliveryWindowDate.$el._flatpickr.toggle()
        }
      })
    },
    onSuccessCartExport() {
      this.onCartExportSuccess()
      this.notifyDownloadsNavMenu()
    },
  },
}
</script>

<style lang="scss">
@import '~@core/scss/vue/libs/vue-flatpicker.scss';
</style>
